import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMemberStats, getAttendanceHistory, getFinancialReport, getFollowups, getCRByNationality, getExpectedByNationality, getCashBook } from '../services/api';
import { FiUsers, FiCheckSquare, FiDollarSign, FiAlertTriangle, FiUserPlus, FiHeart, FiGlobe, FiTrendingUp } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from 'recharts';

// Distinct, print-safe palette for the nationality pie. Ordered so the
// biggest slice always lands on indigo (matches the app accent).
const PIE_COLORS = ['#4f46e5', '#16a34a', '#ea580c', '#0891b2', '#db2777', '#7c3aed', '#65a30d', '#d97706', '#0284c7', '#dc2626', '#7c2d12', '#4b5563'];

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, grandTotal: 0, members: 0, families: 0, inactive: 0, men: 0, women: 0, boys: 0, girls: 0, unknownAge: 0 });
  const [attendance, setAttendance] = useState([]);
  const [finance, setFinance] = useState({ totalIncome: 0, totalExpense: 0, balance: 0 });
  // v1.9.75 — Per-currency month view sourced from Cash Book endpoint so
  // Balance = TRUE closing (opening + income − expense), matching the
  // Cash Book fix in v1.9.74, and USD / K show separately with prefixes.
  const [monthCash, setMonthCash] = useState({
    opening:  { usd_cash: 0, usd_bank: 0, k_cash: 0, k_bank: 0, k_momo: 0 },
    closing:  { usd_cash: 0, usd_bank: 0, k_cash: 0, k_bank: 0, k_momo: 0 },
    creditTotals: { usd_cash: 0, usd_bank: 0, k_cash: 0, k_bank: 0, k_momo: 0 },
    debitTotals:  { usd_cash: 0, usd_bank: 0, k_cash: 0, k_bank: 0, k_momo: 0 },
  });
  const [overdueFollowups, setOverdueFollowups] = useState([]);
  const [crByNationality, setCrByNationality] = useState([]);
  // v1.9.71 — Nationality chart selectors
  const [natPeriod, setNatPeriod] = useState('annually'); // 'monthly' | 'annually'
  const [natView, setNatView] = useState('paid');         // 'paid' | 'expected'

  useEffect(() => {
    getMemberStats().then(r => setStats(r.data || { total: 0, grandTotal: 0, members: 0, families: 0, inactive: 0, men: 0, women: 0, boys: 0, girls: 0, unknownAge: 0 })).catch(() => {});
    getAttendanceHistory({ from: new Date(Date.now() - 60*24*60*60*1000).toISOString().slice(0,10) })
      .then(r => setAttendance(Array.isArray(r.data) ? r.data.slice(0,8).reverse() : [])).catch(() => {});
    getFinancialReport({ from: new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString().slice(0,10) })
      .then(r => setFinance(r.data || { totalIncome: 0, totalExpense: 0, balance: 0 })).catch(() => {});
    // Same window as the header stat cards but sourced from /cashbook so
    // the Balance line is the true closing (includes opening balance).
    const monthFrom = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    getCashBook({ from: monthFrom, to: today })
      .then(r => setMonthCash(prev => ({ ...prev, ...(r.data || {}) })))
      .catch(() => {});
    getFollowups({ overdue_only: true }).then(r => setOverdueFollowups(Array.isArray(r.data) ? r.data.slice(0,5) : [])).catch(() => {});
  }, []);

  // Re-fetch nationality chart when period or view toggles.
  useEffect(() => {
    const fetcher = natView === 'expected'
      ? getExpectedByNationality({ period: natPeriod })
      : getCRByNationality({ period: natPeriod });
    fetcher
      .then(r => setCrByNationality(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCrByNationality([]));
  }, [natPeriod, natView]);

  // v1.9.90 — split "This Month Income" into real per-currency figures
  // sourced from the Cash Book (same window as the header). The old tile
  // showed a single number next to a $ icon which read as dollars when
  // most income is actually Kwacha.
  const monthUsdIncome = (monthCash.creditTotals?.usd_cash || 0) + (monthCash.creditTotals?.usd_bank || 0);
  const monthKwachaIncome = (monthCash.creditTotals?.k_cash || 0) + (monthCash.creditTotals?.k_bank || 0) + (monthCash.creditTotals?.k_momo || 0);
  const fmtMoney = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const monthIncomeDisplay = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontVariantNumeric: 'tabular-nums' }}>
      <span style={{ fontSize: 15, fontWeight: 700, color: '#16a34a' }}>${fmtMoney(monthUsdIncome)}</span>
      <span style={{ fontSize: 15, fontWeight: 700, color: '#2563eb' }}>K{fmtMoney(monthKwachaIncome)}</span>
    </div>
  );

  const statCards = [
    // Card 1 — Active Members (count of members with membership_status='member').
    // Card 2 — Families: distinct family roots where ≥1 member is active.
    // (Renamed per user spec 2026-06-24; previous labels were "Total Members" / "Active Members".)
    { label: t('dashboard.activeMembers'), value: stats.members, icon: <FiUsers />, bg: '#ede9fe', color: '#7c3aed' },
    { label: t('dashboard.families'), value: stats.families, icon: <FiCheckSquare />, bg: '#dcfce7', color: '#16a34a' },
    { label: t('dashboard.thisMonthIncome'), value: monthIncomeDisplay, icon: <FiTrendingUp />, bg: '#dbeafe', color: '#2563eb' },
    { label: t('dashboard.overdueFollowups'), value: overdueFollowups.length, icon: <FiAlertTriangle />, bg: '#fee2e2', color: '#dc2626' },
  ];

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('dashboard.title')}</h1><p>{t('dashboard.welcome')}</p></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate('/members/add')}><FiUserPlus /> {t('nav.addMember')}</button>
          <button className="btn btn-primary" onClick={() => navigate('/spiritual/followups')}><FiHeart /> {t('followup.title')}</button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="stat-cards">
        {statCards.map((s, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
            <div>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid">
        {/* Attendance Chart */}
        <div className="card">
          <div className="card-header">{t('dashboard.recentAttendance')}</div>
          <div className="card-body">
            {attendance.length === 0 ? (
              <div className="empty-state"><div className="empty-icon">📊</div><p>{t('dashboard.noAttendanceData')}</p></div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={attendance} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [v, t('dashboardExtra.attendance')]} labelFormatter={l => `${t('dashboardExtra.dateLabel')}: ${l}`} />
                  <Bar dataKey="actual_count" fill="#4f46e5" radius={[4,4,0,0]} name={t('dashboardExtra.attendance')} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Right panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Finance Summary — v1.9.75 sourced from Cash Book so Balance
              is the TRUE closing (opening + income - expense), and USD/K
              show separately with currency prefixes. */}
          <div className="card">
            <div className="card-header">{t('dashboard.thisMonthFinance')}</div>
            <div className="card-body">
              {(() => {
                const sumUSD = (o) => parseFloat(o.usd_cash || 0) + parseFloat(o.usd_bank || 0);
                const sumK   = (o) => parseFloat(o.k_cash || 0) + parseFloat(o.k_bank || 0) + parseFloat(o.k_momo || 0);
                const fmt = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const rows = [
                  { label: 'Opening',                    usd: sumUSD(monthCash.opening),      k: sumK(monthCash.opening),      color: '#6b7280' },
                  { label: t('dashboard.income'),        usd: sumUSD(monthCash.creditTotals), k: sumK(monthCash.creditTotals), color: '#16a34a' },
                  { label: t('dashboard.expense'),       usd: sumUSD(monthCash.debitTotals),  k: sumK(monthCash.debitTotals),  color: '#dc2626' },
                  { label: t('dashboard.balance'),       usd: sumUSD(monthCash.closing),      k: sumK(monthCash.closing),      color: '#4f46e5', bold: true },
                ];
                return rows.map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < rows.length - 1 ? '1px solid #f3f4f6' : 'none', gap: 12 }}>
                    <span style={{ fontSize: 13, color: '#6b7280', fontWeight: item.bold ? 700 : 400 }}>{item.label}</span>
                    <span style={{ fontWeight: item.bold ? 800 : 700, color: item.color, fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      ${fmt(item.usd)} · K{fmt(item.k)}
                    </span>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Overdue Follow-ups */}
          <div className="card" style={{ flex: 1 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {t('dashboard.overdueFollowups')}
              <button className="btn btn-sm btn-secondary" onClick={() => navigate('/spiritual/followups')}>{t('dashboard.viewAll')}</button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {overdueFollowups.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px' }}><p>{t('dashboard.noOverdueFollowups')}</p></div>
              ) : (
                overdueFollowups.map((f, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: i < overdueFollowups.length-1 ? '1px solid #f3f4f6' : 'none' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{f.family_name}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{t('dashboardExtra.due')}: {f.next_followup_date}</div>
                    </div>
                    <span className="pill pill-red">{t('followup.overdue')}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Demographics Row */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{t('dashboard.demographics')}</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{t('common.total')}: <strong style={{ color: '#4f46e5' }}>{stats.grandTotal}</strong></span>
        </div>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12 }}>
          {[
            { label: t('dashboard.men'), value: stats.men, color: '#2563eb', bg: '#dbeafe' },
            { label: t('dashboard.women'), value: stats.women, color: '#db2777', bg: '#fce7f3' },
            { label: t('dashboard.boys'), value: stats.boys, color: '#0891b2', bg: '#cffafe' },
            { label: t('dashboard.girls'), value: stats.girls, color: '#9333ea', bg: '#f3e8ff' },
            { label: t('dashboard.unknownAge'), value: stats.unknownAge, color: '#6b7280', bg: '#f3f4f6' },
          ].map((s, i) => (
            <div key={i} onClick={() => navigate('/members/status')}
              style={{ textAlign: 'center', background: s.bg, borderRadius: 10, padding: '12px 8px', cursor: 'pointer' }}
              title={t('dashboardExtra.viewDemographics')}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* v1.9.60 — Income (CR) by Nationality, last 12 months.
          Pie chart on Kwacha share (dominant currency); USD + count shown
          in the side legend so both currencies are visible without mixing
          them on the same wheel. */}
      {(() => {
        const kwachaOnly = crByNationality.filter(r => parseFloat(r.kwacha || 0) > 0);
        const kwachaTotal = kwachaOnly.reduce((s, r) => s + parseFloat(r.kwacha || 0), 0);
        const usdTotal = crByNationality.reduce((s, r) => s + parseFloat(r.usd || 0), 0);
        // v1.9.71 — count label swaps depending on view.
        const countKey = natView === 'expected' ? 'subscription_count' : 'cr_count';
        // Amharic/Tigrinya don't pluralise by appending "s", so the whole
        // phrase (number + noun) is resolved from the locale file.
        const nounBase = natView === 'expected' ? 'subscriptions' : 'receipts';
        const countLabel = (n) => t(`natChart.${nounBase}${n === 1 ? 'One' : 'Many'}`, { n: n.toLocaleString() });
        const receiptCount = crByNationality.reduce((s, r) => s + (r[countKey] || 0), 0);
        // Slice label: only draw if slice ≥5% so tiny slices don't collide.
        const renderSliceLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value }) => {
          if (percent < 0.05) return null;
          const RADIAN = Math.PI / 180;
          const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
          const x = cx + radius * Math.cos(-midAngle * RADIAN);
          const y = cy + radius * Math.sin(-midAngle * RADIAN);
          return (
            <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" style={{ fontSize: 12, fontWeight: 700 }}>
              <tspan x={x} dy="-0.4em">{(percent * 100).toFixed(0)}%</tspan>
              <tspan x={x} dy="1.2em" style={{ fontSize: 10, fontWeight: 500 }}>
                K{value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value.toFixed(0)}
              </tspan>
            </text>
          );
        };
        return (
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <FiGlobe /> {t(natView === 'expected' ? 'natChart.titleExpected' : 'natChart.titlePaid')}
              </span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {/* View toggle: Paid (CRs actually received) vs
                    Expected (sum of active subscription pledges,
                    normalised to the selected period). */}
                <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden' }}>
                  {[
                    { key: 'paid',     label: t('natChart.paid') },
                    { key: 'expected', label: t('natChart.expected') },
                  ].map(v => (
                    <button
                      key={v.key}
                      onClick={() => setNatView(v.key)}
                      style={{ padding: '5px 12px', background: natView === v.key ? '#4f46e5' : '#fff', color: natView === v.key ? '#fff' : '#374151', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >{v.label}</button>
                  ))}
                </div>
                <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden' }}>
                  {[
                    { key: 'monthly',  label: t('natChart.monthly') },
                    { key: 'annually', label: t('natChart.annually') },
                  ].map(p => (
                    <button
                      key={p.key}
                      onClick={() => setNatPeriod(p.key)}
                      style={{ padding: '5px 12px', background: natPeriod === p.key ? '#4f46e5' : '#fff', color: natPeriod === p.key ? '#fff' : '#374151', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >{p.label}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="card-body">
              {crByNationality.length === 0 ? (
                <div className="empty-state" style={{ padding: '30px' }}>
                  <div className="empty-icon">🌍</div>
                  <p>
                    {natView === 'expected'
                      ? t('natChart.noSubscriptions')
                      : t(natPeriod === 'monthly' ? 'natChart.noReceiptsMonth' : 'natChart.noReceiptsYear')}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, alignItems: 'center' }} className="cr-pie-grid">
                  {/* PIE — Kwacha share */}
                  <div style={{ position: 'relative' }}>
                    <ResponsiveContainer width="100%" height={320}>
                      <PieChart>
                        <Pie
                          data={kwachaOnly}
                          dataKey="kwacha"
                          nameKey="nationality"
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={125}
                          paddingAngle={2}
                          label={renderSliceLabel}
                          labelLine={false}
                          isAnimationActive={true}
                        >
                          {kwachaOnly.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="#fff" strokeWidth={2} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(v) => [`K${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, t('natChart.kwacha')]}
                          labelFormatter={l => l}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center total overlay */}
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                      <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('natChart.total')}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2 }}>
                        K{kwachaTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      {usdTotal > 0 && (
                        <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, marginTop: 2 }}>
                          +${usdTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* LEGEND with values */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                      {t('natChart.breakdown')}
                    </div>
                    <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                      {crByNationality.map((r, i) => {
                        const k = parseFloat(r.kwacha || 0);
                        const u = parseFloat(r.usd || 0);
                        const kPct = kwachaTotal > 0 ? (k / kwachaTotal) * 100 : 0;
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < crByNationality.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                            <span style={{ width: 12, height: 12, borderRadius: 3, background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.nationality}</div>
                              <div style={{ fontSize: 11, color: '#9ca3af' }}>{countLabel(r[countKey] || 0)}</div>
                            </div>
                            <div style={{ textAlign: 'right', fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>
                              {k > 0 && (
                                <div style={{ color: '#4f46e5', fontWeight: 700 }}>
                                  K{k.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  <span style={{ color: '#9ca3af', fontWeight: 500, marginLeft: 4 }}>· {kPct.toFixed(1)}%</span>
                                </div>
                              )}
                              {u > 0 && (
                                <div style={{ color: '#16a34a', fontWeight: 600 }}>
                                  ${u.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: '2px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>{countLabel(receiptCount)} · {t(`natChart.nationalities${crByNationality.length === 1 ? 'One' : 'Many'}`, { n: crByNationality.length })} · {t(natPeriod === 'monthly' ? 'natChart.perMonth' : 'natChart.perYear')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Dashboard;
